import { useEffect, useState } from "react";
import { useLeadContext } from "../../../contexts/LeadContext";
import ReplyEditor from "./ReplyEditor";
import MessageThread from "./MessageThread";
import LoadingScreen from "../../../ui/loadingScreen/LoadingScreen";
import styles from "./CommunicationDetailPanel.module.css";

const hasSenderEmail = (mailCorp) =>
  Boolean(mailCorp) && typeof mailCorp === "object" && Boolean(mailCorp.email);

const resolveSenderAccount = (mailCorp, fallbackAccount) =>
  hasSenderEmail(mailCorp) ? mailCorp : fallbackAccount ?? null;

const CommunicationDetailPanel = ({
  recipient,
  leadId,
  leadPersons = [],
  personPicker = {},
  onAddPerson,
  onMarkRecipient,
  accountId = null,
  leadAccount = null,
  leadCompany = null,
  defaultSenderAccount = null,
  composerMode = "reply",
  onComposerModeChange,
}) => {
  const {
    selectedCommunicationSubject,
    conversationMessages,
    conversationLoading,
    conversationError,
  } = useLeadContext();

  const subject =
    selectedCommunicationSubject?.subject ||
    selectedCommunicationSubject?.normalized_subject ||
    "Sin asunto";

  const templateGroupId = selectedCommunicationSubject?.template_group_id ?? null;
  const subjectGroupId =
    selectedCommunicationSubject?.subject_group_id ??
    selectedCommunicationSubject?.id ??
    null;
  const threadSenderAccount = resolveSenderAccount(
    selectedCommunicationSubject?.mail_corp,
    defaultSenderAccount,
  );
  const isNewMail = composerMode === "new" || !selectedCommunicationSubject;

  const [selectedInboundMessage, setSelectedInboundMessage] = useState(null);
  const [isThreadMessageOpen, setIsThreadMessageOpen] = useState(false);

  useEffect(() => {
    setSelectedInboundMessage(null);
    setIsThreadMessageOpen(false);
  }, [selectedCommunicationSubject]);

  return (
    <section className={styles.communicationDetailPanel}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h3 className={styles.title}>{isNewMail ? "Nuevo correo" : subject}</h3>

          <p className={styles.subtitle}>
            {isNewMail
              ? "Crea un hilo nuevo con las personas del lead"
              : recipient?.name || "Sin contacto"}
          </p>
        </div>
      </header>

      <div className={styles.threadContainer}>
          {conversationLoading ? (
            <LoadingScreen
              embedded
              mainText="Cargando conversación..."
              subText=""
            />
          ) : conversationError ? (
            <div className={styles.emptyState}>
              No se pudo cargar la conversación.
            </div>
          ) : (
            <MessageThread
              messages={conversationMessages}
              recipient={recipient}
              selectedMessage={selectedInboundMessage}
              onSelectMessage={setSelectedInboundMessage}
              onOpenMessageChange={setIsThreadMessageOpen}
            />
          )}
      </div>

      <div className={styles.replyContainer}>
        <ReplyEditor
          leadPersons={leadPersons}
          senderAccount={threadSenderAccount}
          defaultSenderAccount={defaultSenderAccount}
          composerMode={isNewMail ? "new" : "reply"}
          onComposerModeChange={onComposerModeChange}
          personPicker={personPicker}
          onAddPerson={onAddPerson}
          onMarkRecipient={onMarkRecipient}
          leadId={leadId}
          selectedMessage={selectedInboundMessage}
          templateGroupId={templateGroupId}
          subjectGroupId={subjectGroupId}
          accountId={accountId}
          contactLayer={leadAccount?.id ? leadAccount : null}
          leadCompany={leadCompany}
          disabled={conversationLoading}
          collapseRecipients={isThreadMessageOpen && !isNewMail && !conversationLoading}
        />
      </div>
    </section>
  );
};

export default CommunicationDetailPanel;
